import React, { useState, useRef, useEffect } from 'react';
import { Video, Phone, MessageCircle, Check, ArrowRight, Play, Clock, Users, Infinity, Sparkles, Zap } from 'lucide-react';

const CourseStructureSection = () => {
  const [activeCard, setActiveCard] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const cards = [
    {
      id: 'video',
      icon: Video,
      number: '75',
      label: 'уроков',
      title: 'Видео-курс',
      color: 'amber',
      gradient: 'from-amber-500 to-orange-600',
      shadowColor: 'shadow-amber-500/30',
      features: [
        { icon: Play, text: 'Смотрите в своём темпе' },
        { icon: Clock, text: 'Когда удобно' },
        { icon: Infinity, text: 'Доступ навсегда' },
      ],
      bottomText: '10 модулей. От старта до продаж.',
    },
    {
      id: 'calls',
      icon: Phone,
      number: '9',
      label: 'созвонов',
      title: 'Разборы',
      color: 'emerald',
      gradient: 'from-emerald-500 to-teal-600',
      shadowColor: 'shadow-emerald-500/30',
      features: [
        { icon: Users, text: 'Живые созвоны' },
        { icon: Video, text: 'Или записи' },
        { icon: Check, text: 'Разбираем ошибки' },
      ],
      bottomText: '9 тем от регистрации до масштабирования.',
    },
    {
      id: 'chat',
      icon: MessageCircle,
      number: '24/7',
      label: 'поддержка',
      title: 'Чат',
      color: 'violet',
      gradient: 'from-violet-500 to-purple-600',
      shadowColor: 'shadow-violet-500/30',
      features: [
        { icon: MessageCircle, text: 'Застряли? Поможем' },
        { icon: Zap, text: 'Быстрые ответы' },
        { icon: Users, text: 'Общение с учениками' },
      ],
      bottomText: 'Не остаётесь один на один с проблемой.',
    },
  ];

  return (
    <section 
      ref={sectionRef}
      className="relative py-20 sm:py-28 overflow-hidden bg-[#030304]" 
      id="course-structure"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/3 left-0 w-[600px] h-[600px] bg-amber-500/[0.02] rounded-full blur-[150px]" />
        <div className="absolute bottom-1/3 right-0 w-[500px] h-[500px] bg-violet-500/[0.02] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 mb-6 backdrop-blur-xl">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300 font-bold text-sm tracking-wide">ЧТО ВНУТРИ</span>
          </div>
          
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-4">
            <span className="text-white">Три </span>
            <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 bg-clip-text text-transparent">инструмента</span>
          </h2>
          <p className="text-xl text-zinc-400">Всё для запуска бизнеса на Amazon</p>
        </div>

        {/* Cards */}
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {cards.map((card, index) => {
            const Icon = card.icon;
            const isActive = activeCard === card.id;
            
            return (
              <div
                key={card.id}
                className={`group relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
                onMouseEnter={() => setActiveCard(card.id)}
                onMouseLeave={() => setActiveCard(null)}
              >
                {/* Glow effect */}
                <div className={`absolute -inset-4 bg-gradient-to-r ${card.gradient} rounded-[2rem] blur-3xl transition-all duration-700 ${
                  isActive ? 'opacity-30 scale-105' : 'opacity-0 scale-100'
                }`} />
                
                {/* Card */}
                <div className={`relative h-full rounded-3xl border-2 overflow-hidden transition-all duration-500 backdrop-blur-xl ${
                  isActive 
                    ? `bg-zinc-900/95 border-${card.color}-500/50 -translate-y-3 ${card.shadowColor} shadow-2xl` 
                    : 'bg-zinc-900/60 border-zinc-800 hover:border-zinc-700'
                }`}>
                  
                  {/* Animated top bar */}
                  <div className={`h-1.5 bg-gradient-to-r ${card.gradient} transition-transform duration-500 origin-left ${
                    isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`} />
                  
                  <div className="p-8">
                    {/* Header row */}
                    <div className="flex items-start justify-between mb-8">
                      {/* Icon */}
                      <div className={`relative w-20 h-20 rounded-2xl bg-gradient-to-br ${card.gradient} flex items-center justify-center transition-all duration-500 ${
                        isActive ? 'scale-110 rotate-3' : 'group-hover:scale-105'
                      }`}>
                        <Icon className="w-10 h-10 text-white" />
                        {isActive && (
                          <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${card.gradient} animate-ping opacity-20`} />
                        )}
                      </div>
                      
                      {/* Big number */}
                      <div className="text-right">
                        <p className={`text-6xl font-black bg-gradient-to-br ${card.gradient} bg-clip-text text-transparent`}>
                          {card.number}
                        </p>
                        <p className="text-zinc-500 text-sm font-medium">{card.label}</p>
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-3xl font-black text-white mb-6">{card.title}</h3>
                    
                    {/* Features */}
                    <div className="space-y-4 mb-8">
                      {card.features.map((feature, i) => {
                        const FeatureIcon = feature.icon;
                        return (
                          <div 
                            key={i} 
                            className="flex items-center gap-4"
                            style={{ transitionDelay: `${i * 50}ms` }}
                          >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-500 ${
                              isActive 
                                ? `bg-gradient-to-br ${card.gradient}` 
                                : 'bg-zinc-800'
                            }`}>
                              <FeatureIcon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-zinc-400'}`} />
                            </div>
                            <p className={`text-lg transition-colors duration-300 ${
                              isActive ? 'text-white font-medium' : 'text-zinc-400'
                            }`}>
                              {feature.text}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Bottom text */}
                    <div className={`pt-6 border-t transition-colors duration-300 ${
                      isActive ? 'border-zinc-700' : 'border-zinc-800'
                    }`}>
                      <p className={`text-sm transition-colors duration-300 ${
                        isActive ? 'text-zinc-300' : 'text-zinc-500'
                      }`}>
                        {card.bottomText}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary bar */}
        <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="relative">
            {/* Glow */}
            <div className="absolute -inset-2 bg-gradient-to-r from-amber-500/20 via-emerald-500/20 to-violet-500/20 rounded-3xl blur-2xl" />
            
            <div className="relative p-6 sm:p-8 rounded-3xl bg-zinc-900/90 border border-zinc-800 backdrop-blur-xl">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                
                {/* Stats */}
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-amber-500 to-orange-500" />
                    <span className="text-white font-bold">75 уроков</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500" />
                    <span className="text-white font-bold">9 созвонов</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-violet-500 to-purple-500" />
                    <span className="text-white font-bold">Чат 24/7</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Infinity className="w-5 h-5 text-zinc-400" />
                    <span className="text-white font-bold">Доступ навсегда</span>
                  </div>
                </div>
                
                {/* CTA */}
                <a 
                  href="#pricing"
                  className="group flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-black rounded-xl shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-1 active:scale-[0.98] transition-all duration-300"
                  style={{ touchAction: 'manipulation' }}
                >
                  <span>Выбрать тариф</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CourseStructureSection;
